import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { scrub, sentryEnabled } from "@/lib/sentry";

interface LogContext { [key: string]: unknown }

function toJson(value: unknown): Json | null {
  if (value == null) return null;
  return JSON.parse(JSON.stringify(value)) as Json;
}

async function persist(severity: string, message: string, stack?: string, metadata?: LogContext) {
  try {
    await supabase.from("error_logs").insert({
      severity,
      message,
      source: typeof window !== "undefined" ? window.location.pathname : "unknown",
      url: typeof window !== "undefined" ? window.location.href : null,
      stack: stack ?? null,
      metadata: toJson(metadata),
    });
  } catch {
    // Never throw from logger
  }
}

export const logger = {
  error(message: string, error?: unknown, context?: LogContext) {
    const stack = error instanceof Error ? error.stack : undefined;
    const metadata: LogContext = {
      ...context,
      ...(error instanceof Error
        ? { errorName: error.name, errorMessage: error.message }
        : error !== undefined ? { error: String(error) } : {}),
    };
    console.error(`[ERROR] ${message}`, error);
    persist("error", message, stack, Object.keys(metadata).length ? metadata : undefined);

    if (sentryEnabled) {
      Sentry.captureException(error instanceof Error ? error : new Error(message), {
        extra: scrub({ loggerMessage: message, ...metadata }),
      });
    }
  },
  warn(message: string, context?: LogContext) {
    console.warn(`[WARN] ${message}`, context);
    persist("warning", message, undefined, context);

    if (sentryEnabled) {
      Sentry.captureMessage(message, { level: "warning", extra: scrub(context ?? {}) });
    }
  },
};
